// AlertScopesManagement.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Select, Tabs } from 'antd';
import { fetchScopeAffirmations, updateScopeAffirmation, fetchAllClauses } from '../functions/alertScopeService';

const AlertScopesManagement = ({ isVisible, onClose }) => {
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [clausesOptions, setClausesOptions] = useState([]);
  const [clausesData, setClausesData] = useState([]);
  const [clausesLoading, setClausesLoading] = useState(false);

  useEffect(() => {
    if (isVisible) {
    loadScopes();
    }
  }, [isVisible]);

  useEffect(() => {
    const loadClauses = async () => {
      try {
        const clauses = await fetchAllClauses();
        const options = clauses.map(c => ({ value: String(c.ID), label: `${c.ID} - ${c.clause_txt || ''}` }));
        setClausesOptions(options);
      } catch (e) {
        // ignore
      }
    };
    loadClauses();
  }, []);

  const loadScopes = async () => {
    setLoading(true);
    try {
      const data = await fetchScopeAffirmations();
      setScopes(data);
    } catch (error) {
      console.error('Error loading scopes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClausesData = async () => {
    setClausesLoading(true);
    try {
      const scopesData = await fetchScopeAffirmations();
      const clauses = await fetchAllClauses();
      
      // Create a map of clause assignments
      const clauseAssignments = {};
      scopesData.forEach(scope => {
        if (scope.affirmative_clauses) {
          const clauseIds = scope.affirmative_clauses.split(',').filter(Boolean);
          clauseIds.forEach(clauseId => {
            if (!clauseAssignments[clauseId]) {
              clauseAssignments[clauseId] = [];
            }
            clauseAssignments[clauseId].push({
              scope: scope.Scope,
              alert_type: scope.alert_type
            });
          });
        }
      });
      
      // Create clauses data with assignments - only show clauses that are assigned
      const clausesWithAssignments = clauses
        .filter(clause => clauseAssignments[clause.ID] && clauseAssignments[clause.ID].length > 0)
        .map(clause => ({
          ...clause,
          assignments: clauseAssignments[clause.ID]
        }));
      
      setClausesData(clausesWithAssignments);
    } catch (error) {
      console.error('Error loading clauses data:', error);
    } finally {
      setClausesLoading(false);
    }
  };

  const handleSave = async (scope, alert_type, clauses) => {
    try {
      await updateScopeAffirmation({
        scope: scope,
        alert_type: alert_type,
        affirmative_clauses: clauses
      });
      setEditingKey(null);
      loadScopes(); // Refresh data
    } catch (error) {
      console.error('Error saving scope:', error);
    }
  };

  const columns = [
    {
      title: 'Scope',
      dataIndex: 'Scope',
      key: 'Scope',
      width: 150,
    },

    {
      title: 'Alert Type',
      dataIndex: 'alert_type',
      key: 'alert_type',
      width: 220,
    },
    {
      title: 'Affirmative Clauses',
      dataIndex: 'affirmative_clauses',
      key: 'affirmative_clauses',
      render: (text, record) => {
        if (editingKey === `${record.Scope}__${record.alert_type}`) {
          return (
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="Select clauses"
              style={{ width: "100%" }}
              defaultValue={(text || "").split(",").filter(Boolean)}
              options={clausesOptions}
              filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
              onChange={(values) => handleSave(record.Scope, record.alert_type, values.join(","))}
              autoFocus
            />
          );
        }
        return text || 'None';
      },
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 200,
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => setEditingKey(`${record.Scope}__${record.alert_type}`)}
        >
          {editingKey === record.Scope ? 'Saving...' : 'Edit'}
        </Button>
      ),
    },
  ];

  const clausesColumns = [
    {
      title: 'Clause ID',
      dataIndex: 'ID',
      key: 'ID',
      width: 100,
    },
    {
      title: 'Clause Text',
      dataIndex: 'clause_txt',
      key: 'clause_txt',
      render: (text) => text || 'No text available',
    },
    {
      title: 'Scope/Alert-Type Assignments',
      dataIndex: 'assignments',
      key: 'assignments',
      render: (assignments) => (
        <div>
          {assignments.map((assignment, index) => (
            <div key={index} style={{ marginBottom: '4px' }}>
              <span style={{ fontWeight: 'bold' }}>{assignment.scope}</span> / <span style={{ color: '#1890ff' }}>{assignment.alert_type}</span>
            </div>
          ))}
        </div>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'scopes',
      label: 'Scope + Alert-Type Management',
      children: (
        <Table
          dataSource={scopes}
          columns={columns}
          rowKey={(r) => `${r.Scope}__${r.alert_type}`}
          loading={loading}
          pagination={{ pageSize: 20 }}
          size="small"
        />
      ),
    },
    {
      key: 'clauses',
      label: 'Clauses',
      children: (
        <Table
          dataSource={clausesData}
          columns={clausesColumns}
          rowKey="ID"
          loading={clausesLoading}
          pagination={{ pageSize: 20 }}
          size="small"
        />
      ),
    },
  ];

  return (
    <Modal
      title="Manage Scope + Alert-Type Affirmations"
      open={isVisible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width="80%"
    >
      <Tabs 
        defaultActiveKey="scopes"
        items={tabItems}
        onChange={(key) => {
          if (key === 'clauses' && clausesData.length === 0) {
            loadClausesData();
          }
        }}
      />
    </Modal>
  );
};

export default AlertScopesManagement;


